import { eventEmitter } from '@/event-bus'
import i18n from '@/i18n/i18n'
import BaseInterestRateService from '@/services/admin/BaseInterestRateService.js'
import { build_query, errorMessage, getValidationErrorMessage } from '@/utilities/helpers.js'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = ['retailer_id']
export const allowedSorts = '-created_at'
export const allowedIncludes = ''
export const allowedAppends = ''

export const state = {
  baseInterestRates: null,
  baseInterestRate: {},
  meta: null
}

export const mutations = {
  SET_BASE_INTEREST_RATES(state, baseInterestRates) {
    state.baseInterestRates = baseInterestRates.data
    state.meta = baseInterestRates.meta
  },
  SET_BASE_INTEREST_RATES_IMAGE(state, interestRates) {
    state.interestRateImage = interestRates.interestRateImage
    state.interestRateImageMustBeSeen = interestRates.interestRateImageMustBeSeen
    state.isInterestRateImageUploaded = interestRates.isInterestRateImageUploaded
    state.interestRateImageUrl = interestRates.interestRateImageUrl
  },
  SET_BASE_INTEREST_RATE(state, baseInterestRate) {
    state.baseInterestRate = baseInterestRate
  },
  ADD_BASE_INTEREST_RATE(state, baseInterestRate) {
    state.baseInterestRates.push(baseInterestRate)
  }
}

export const actions = {
  getBaseInterestRates({ commit, dispatch, state }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends)

    return new Promise((resolve, reject) => {
      BaseInterestRateService.getBaseInterestRates(newQuery)
        .then(response => {
          commit('SET_BASE_INTEREST_RATES', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.base_interest_rate.base_interest_rate')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getBaseInterestRate({ commit }, id) {
    return new Promise(resolve => {
      BaseInterestRateService.getBaseInterestRate(id).then(response => {
        commit('SET_BASE_INTEREST_RATE', response.data)

        resolve(response.data)
      })
    })
  },
  createBaseInterestRate({ dispatch, commit, state }, baseInterestRate) {
    return new Promise((resolve, reject) => {
      BaseInterestRateService.createBaseInterestRate(baseInterestRate)
        .then(response => {
          if (state.baseInterestRates) {
            commit('ADD_BASE_INTEREST_RATE', response.data)
          }
          const notification = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('store.admin.base_interest_rate.base_interest_rate')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const duplicateInterestRateMessage = getValidationErrorMessage(err, 'duplicate_interest_rate')

          if (duplicateInterestRateMessage !== null) {
            eventEmitter.emit('show-duplicate-interest-rate-modal', duplicateInterestRateMessage)
          }

          const notification = {
            type: 'danger',
            message: t('store.shared.not_added', {
              element: t('store.admin.base_interest_rate.base_interest_rate')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  startImportProcess({ dispatch }, form) {
    return new Promise((resolve, reject) => {
      BaseInterestRateService.startImportProcess(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.import_start', {
              element: t('store.admin.base_interest_rate.base_interest_rate')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.importing_start_error', {
              element: t('store.admin.base_interest_rate.base_interest_rate')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  uploadInterestRate({ dispatch, commit, state }, form) {
    return new Promise((resolve, reject) => {
      BaseInterestRateService.uploadImageForPriceComparison(form)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.base_interest_rate.base_interest_rate_image_and_option')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.uploading_error', {
                element: t('store.admin.base_interest_rate.base_interest_rate_image_and_option')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  removeInterestRateImage({ dispatch }) {
    return new Promise((resolve, reject) => {
      BaseInterestRateService.removePriceComparisonImage()
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.base_interest_rate.base_interest_rate_image_and_option')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.deleted', {
              element: t('store.admin.base_interest_rate.base_interest_rate_image')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getInterestRateImageSettings({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      BaseInterestRateService.getInterestRateImageSettings()
        .then(response => {
          commit('SET_BASE_INTEREST_RATES_IMAGE', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.base_interest_rate.base_interest_rate_image')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  toggleImportBaseInterestRate({ dispatch }) {
    eventEmitter.emit('toggle-import-base-interest-rate')
  }
}
